import { brandSurface, Button, colors, Icon, LogoMark, Screen, Text, useRouter } from '@khaddi/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { View } from 'react-native';
import { Section } from '../components/bits';
import { useCart } from '../state/cart';
import { canPromptInstall, installPlatform, onInstallChange, promptInstall, type InstallPlatform } from '../state/install';

const Step = ({ n, children }: { n: number; children: ReactNode }) => (
  <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
    <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: colors.sky, alignItems: 'center', justifyContent: 'center' }}>
      <Text variant="bodyStrong">{n}</Text>
    </View>
    <Text variant="body" style={{ flex: 1, paddingTop: 3 }}>
      {children}
    </Text>
  </View>
);

const STEPS: Record<Exclude<InstallPlatform, 'installed'>, { title: string; steps: ReactNode[] }> = {
  in_app_browser: {
    title: 'Mở bằng trình duyệt để cài app',
    steps: [
      <>Bấm nút <Text variant="bodyStrong">⋯</Text> ở góc trên bên phải.</>,
      <>Chọn <Text variant="bodyStrong">Mở bằng trình duyệt</Text> (Safari hoặc Chrome).</>,
      <>Làm theo hướng dẫn cài app hiện ra.</>,
    ],
  },
  ios: {
    title: 'Cài app trên iPhone',
    steps: [
      <>Bấm nút <Text variant="bodyStrong">Chia sẻ</Text> (ô vuông có mũi tên lên) ở thanh dưới Safari.</>,
      <>Kéo xuống, chọn <Text variant="bodyStrong">Thêm vào MH chính</Text>.</>,
      <>Bấm <Text variant="bodyStrong">Thêm</Text>. Biểu tượng Khao Ddi hiện trên màn hình chính.</>,
    ],
  },
  android: {
    title: 'Cài app trên Android',
    steps: [
      <>Bấm nút <Text variant="bodyStrong">⋮</Text> ở góc trên bên phải Chrome.</>,
      <>Chọn <Text variant="bodyStrong">Cài đặt ứng dụng</Text> hoặc <Text variant="bodyStrong">Thêm vào màn hình chính</Text>.</>,
      <>Bấm <Text variant="bodyStrong">Cài đặt</Text>.</>,
    ],
  },
  other: {
    title: 'Cài app trên máy này',
    steps: [
      <>Mở menu của trình duyệt.</>,
      <>Chọn <Text variant="bodyStrong">Cài đặt ứng dụng</Text> hoặc <Text variant="bodyStrong">Thêm vào màn hình chính</Text>.</>,
    ],
  },
};

/** Landing page of the counter QR code: picks the store's branch and walks the customer through installing. */
export const InstallScreen = () => {
  const { route, navigate } = useRouter();
  const cart = useCart();
  const [platform] = useState(installPlatform);
  const [promptable, setPromptable] = useState(canPromptInstall);
  const [installed, setInstalled] = useState(platform === 'installed');

  const branchId = route.query.branch;
  useEffect(() => {
    if (branchId) cart.setBranchId(branchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => onInstallChange(() => setPromptable(canPromptInstall())), []);

  // iOS may start the installed app on the page it was added from: go straight to the menu
  useEffect(() => {
    if (platform === 'installed') navigate('/', { replace: true });
  }, [platform, navigate]);

  const install = async () => {
    if (await promptInstall()) setInstalled(true);
    setPromptable(canPromptInstall());
  };

  const guide = platform === 'installed' ? null : STEPS[platform];

  return (
    <Screen>
      <View style={{ padding: 20, gap: 16, alignItems: 'stretch', width: '100%', maxWidth: 480, alignSelf: 'center' }}>
        <View style={{ alignItems: 'center', gap: 8, paddingTop: 12 }}>
          <View style={{ ...brandSurface, width: 96, height: 96, borderRadius: 28, alignItems: 'center', justifyContent: 'center' }}>
            <LogoMark size={68} />
          </View>
          <Text variant="display" style={{ textAlign: 'center' }}>
            Khao Ddi
          </Text>
          <Text variant="body" style={{ textAlign: 'center' }}>
            Order ngay tại quán, tích điểm mỗi lần mua và đổi quà.
          </Text>
        </View>

        {installed ? (
          <Section style={{ gap: 10, alignItems: 'center' }}>
            <Icon name="checkCircle" size={36} color={colors.primaryText} />
            <Text variant="title" style={{ textAlign: 'center' }}>
              App đã được cài
            </Text>
            <Text variant="caption" style={{ textAlign: 'center' }}>
              Mở Khao Ddi từ màn hình chính để order và tích điểm.
            </Text>
          </Section>
        ) : promptable ? (
          <Button label="Cài app Khao Ddi" icon="upload" onPress={() => void install()} />
        ) : guide ? (
          <Section style={{ gap: 12 }}>
            <Text variant="title">{guide.title}</Text>
            {guide.steps.map((step, i) => (
              <Step key={i} n={i + 1}>
                {step}
              </Step>
            ))}
          </Section>
        ) : null}

        <Button label={installed ? 'Vào app' : 'Dùng ngay, không cần cài'} variant={installed ? 'primary' : 'outline'} onPress={() => navigate('/', { replace: true })} />
      </View>
    </Screen>
  );
};
